import { STAGE_KINDS, type StageKind, type WorkflowItem } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, get, post, put } from "../../api";
import { Alert, Badge, Button, Checkbox, Dialog, Field, Loading, PageHeader, Panel, PanelHeader, SelectField, StageBadge, cx } from "../../components/ui";

interface DraftStage {
  id?: string;
  name: string;
  kind: StageKind;
  jobCount: number;
}

/** Job types and their stages. Stages with jobs can be renamed and reordered but not removed. */
export function WorkflowsSettings() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["workflows", "all"], queryFn: () => get<WorkflowItem[]>("/api/workflows?archived=1") });
  const [editing, setEditing] = useState<WorkflowItem | "new" | null>(null);
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ["workflows"] });
  };
  const archive = useMutation({ mutationFn: (w: WorkflowItem) => post(`/api/workflows/${w.id}/archive`, { archived: !w.archived }), onSuccess: refresh });
  const order = useMutation({ mutationFn: (ids: string[]) => post("/api/workflows/order", { ids }), onSuccess: refresh });

  if (list.isLoading) return <Loading />;
  if (list.error) return <Alert>{errorMessage(list.error)}</Alert>;
  const flows = list.data!;
  const move = (i: number, d: -1 | 1) => {
    const ids = flows.map((w) => w.id);
    const j = i + d;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    order.mutate(ids);
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Workflows"
        description="Each job type has its own stages. Jobs start in the first stage."
        action={
          <Button variant="primary" onClick={() => setEditing("new")}>
            + New workflow
          </Button>
        }
      />
      {(archive.error || order.error) && <Alert>{errorMessage(archive.error ?? order.error)}</Alert>}
      {flows.map((w, i) => (
        <Panel key={w.id} className={cx(w.archived && "opacity-60")}>
          <PanelHeader
            title={w.name}
            description={[w.insurance ? "Insurance section on jobs" : null, w.qbCreateOnNew ? "QuickBooks job created when the job is made" : w.qbCreateStageId ? `QuickBooks job created at ${w.stages.find((s) => s.id === w.qbCreateStageId)?.name}` : "No automatic QuickBooks job"]
              .filter(Boolean)
              .join(" · ")}
            action={
              <div className="flex flex-wrap items-center gap-1">
                {w.archived && <Badge tone="off">Archived</Badge>}
                <button className="rounded px-2 py-1 text-muted hover:bg-mist" onClick={() => move(i, -1)} aria-label={`Move ${w.name} up`}>
                  ↑
                </button>
                <button className="rounded px-2 py-1 text-muted hover:bg-mist" onClick={() => move(i, 1)} aria-label={`Move ${w.name} down`}>
                  ↓
                </button>
                <Button className="px-3 py-1.5" onClick={() => archive.mutate(w)}>
                  {w.archived ? "Unarchive" : "Archive"}
                </Button>
                <Button className="px-3 py-1.5" onClick={() => setEditing(w)}>
                  Edit
                </Button>
              </div>
            }
          />
          <div className="flex flex-wrap gap-1.5 px-5 py-4">
            {w.stages.map((s) => (
              <span key={s.id} className="inline-flex items-center gap-1">
                <StageBadge name={s.name} kind={s.kind} />
                {s.jobCount > 0 && <span className="text-xs text-muted">{s.jobCount}</span>}
              </span>
            ))}
          </div>
        </Panel>
      ))}
      {editing && <WorkflowDialog workflow={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={refresh} />}
    </div>
  );
}

function WorkflowDialog({ workflow, onClose, onSaved }: { workflow: WorkflowItem | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const [name, setName] = useState(workflow?.name ?? "");
  const [insurance, setInsurance] = useState(workflow?.insurance ?? false);
  const [stages, setStages] = useState<DraftStage[]>(
    workflow?.stages.map((s) => ({ id: s.id, name: s.name, kind: s.kind, jobCount: s.jobCount })) ?? [
      { name: "Lead", kind: "open", jobCount: 0 },
      { name: "Sold", kind: "open", jobCount: 0 },
      { name: "Closed", kind: "closed", jobCount: 0 },
      { name: "Lost", kind: "lost", jobCount: 0 },
    ],
  );
  // Tracked by position, since new stages have no ID yet.
  const [onNew, setOnNew] = useState(workflow?.qbCreateOnNew ?? false);
  const [qbIndex, setQbIndex] = useState<number | null>(() => {
    // New workflows create the QuickBooks job at Sold.
    if (!workflow) return 1;
    const i = workflow.stages.findIndex((s) => s.id === workflow.qbCreateStageId);
    return i < 0 ? null : i;
  });

  // Where jobs move when a proposal is sent and signed (new workflows: Proposal and Sold).
  const indexOf = (id: string | null | undefined, fallback: number | null) => (workflow ? (id ? workflow.stages.findIndex((s) => s.id === id) : -1) : (fallback ?? -1));
  const [sentIndex, setSentIndex] = useState<number | null>(() => {
    const i = indexOf(workflow?.proposalSentStageId, null);
    return i < 0 ? null : i;
  });
  const [signedIndex, setSignedIndex] = useState<number | null>(() => {
    const i = indexOf(workflow?.proposalSignedStageId, 1);
    return i < 0 ? null : i;
  });
  const keepIndexes = (fn: (q: number | null) => number | null) => {
    setQbIndex(fn);
    setSentIndex(fn);
    setSignedIndex(fn);
  };

  const save = useMutation({
    mutationFn: () => {
      const body = { name, insurance, qbCreateOnNew: onNew, qbCreateStageIndex: onNew ? null : qbIndex, proposalSentStageIndex: sentIndex, proposalSignedStageIndex: signedIndex, stages: stages.map(({ id, name, kind }) => ({ ...(id ? { id } : {}), name, kind })) };
      return workflow ? put(`/api/workflows/${workflow.id}`, body) : post("/api/workflows", body);
    },
    onSuccess: async () => {
      await onSaved();
      onClose();
    },
  });

  const update = (i: number, patch: Partial<DraftStage>) => setStages((s) => s.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= stages.length) return;
    setStages((s) => {
      const next = [...s];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
    keepIndexes((q) => (q === i ? j : q === j ? i : q));
  };
  const remove = (i: number) => {
    setStages((s) => s.filter((_, k) => k !== i));
    keepIndexes((q) => (q === null || q === i ? null : q > i ? q - 1 : q));
  };

  return (
    <Dialog
      wide
      title={workflow ? `Edit ${workflow.name}` : "New workflow"}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()} disabled={!name.trim() || stages.length < 2}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <Field label="Name (job type)" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        <Checkbox label="Insurance jobs" description="Shows carrier, claim #, policy #, date of loss, and deductible on its jobs." checked={insurance} onChange={setInsurance} />
        <div>
          <div className="mb-1 text-sm font-semibold text-ink-soft">Stages, in order</div>
          <ul className="flex flex-col gap-1.5">
            {stages.map((s, i) => (
              <li key={s.id ?? `new-${i}`} className="flex flex-wrap items-center gap-1.5">
                <span className="w-5 text-right text-sm text-muted">{i + 1}</span>
                <input value={s.name} onChange={(e) => update(i, { name: e.target.value })} className="min-w-40 flex-1 rounded-[var(--radius-control)] border border-line px-2.5 py-1.5 text-sm" aria-label={`Stage ${i + 1} name`} />
                <select value={s.kind} onChange={(e) => update(i, { kind: e.target.value as StageKind })} className="rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5 text-sm" aria-label={`Stage ${i + 1} kind`}>
                  {STAGE_KINDS.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </select>
                <button type="button" className="rounded px-1.5 text-muted hover:bg-mist" onClick={() => move(i, -1)} aria-label="Move up">
                  ↑
                </button>
                <button type="button" className="rounded px-1.5 text-muted hover:bg-mist" onClick={() => move(i, 1)} aria-label="Move down">
                  ↓
                </button>
                <button
                  type="button"
                  className="rounded px-1.5 text-muted hover:bg-danger-wash hover:text-danger disabled:opacity-30"
                  onClick={() => remove(i)}
                  disabled={s.jobCount > 0}
                  title={s.jobCount > 0 ? `${s.jobCount} job(s) are in this stage` : "Remove"}
                  aria-label={`Remove ${s.name}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          <button type="button" className="mt-2 text-sm font-semibold text-brand hover:underline" onClick={() => setStages((s) => [...s.slice(0, -2), { name: "", kind: "open", jobCount: 0 }, ...s.slice(-2)])}>
            + Add a stage
          </button>
          <p className="mt-1 text-sm text-muted">New stages go before the last two (usually Closed and Lost). The first stage must be open.</p>
        </div>
        <SelectField
          label="Create the job in QuickBooks"
          hint="With its customer, if they're not there yet. A customer or job already in QuickBooks with the same name is linked, not duplicated."
          value={onNew ? "new" : (qbIndex ?? "")}
          onChange={(e) => {
            setOnNew(e.target.value === "new");
            setQbIndex(e.target.value === "" || e.target.value === "new" ? null : Number(e.target.value));
          }}
        >
          <option value="new">As soon as the job is made</option>
          <option value="">Never automatically (Create in QuickBooks on the job)</option>
          {stages.map((s, i) => (
            <option key={s.id ?? `new-${i}`} value={i}>
              When it reaches {s.name || `Stage ${i + 1}`}
            </option>
          ))}
        </SelectField>
        {(
          [
            ["When a proposal is sent, move the job to", sentIndex, setSentIndex],
            ["When a proposal is signed, move the job to", signedIndex, setSignedIndex],
          ] as const
        ).map(([label, value, set]) => (
          <SelectField key={label} label={label} hint="Only forward: a job already at that stage or past it (or closed or lost) stays where it is." value={value ?? ""} onChange={(e) => set(e.target.value === "" ? null : Number(e.target.value))}>
            <option value="">Don't move it</option>
            {stages.map((s, i) =>
              s.kind === "open" ? (
                <option key={s.id ?? `new-${i}`} value={i}>
                  {s.name || `Stage ${i + 1}`}
                </option>
              ) : null,
            )}
          </SelectField>
        ))}
      </div>
    </Dialog>
  );
}
